/**
 * SimpleWithdrawalDialog — Easy Mode withdrawal (fallback for failed auto-withdrawals)
 *
 * Key summary + recipient address + fee breakdown + one "Unlock" button.
 * Always uses relay (gas-free). Falls back to direct if relay unavailable.
 */

import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Skeleton,
} from '@mui/material';
import { FlashOn, LocalGasStation } from '@mui/icons-material';
import { getChainLogoUrl } from './ChainLogos';
import { getChainType } from '../utils/chainDetect';
import RelayStatusTracker from './RelayStatusTracker';
import { estimateFeeBreakdown, estimateSolanaFeeBreakdown, getProtocolFeeBpsFromContract } from '../services/relayService';
import multiTokenTreasuryService from '../services/multiTokenTreasuryService';
import solanaInternullService from '../services/solanaInternullService';

const SimpleWithdrawalDialog = ({
  open,
  onClose,
  selectedKey,
  recipientAddress,
  onRecipientChange,
  onExecuteWithdrawal,
  onSwitchToDirect,
  withdrawing,
  relayInfo,
  relayerHasBalance,
  relayJobId,
  relayNodeUrl,
  isDark,
}) => {
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  useEffect(() => {
    if (!open || !selectedKey || !relayInfo || !relayerHasBalance) {
      setFeeBreakdown(null);
      return;
    }

    const fetchFees = async () => {
      setFeeLoading(true);
      try {
        const nativeSymbols = ['ETH', 'BNB', 'TBNB', 'POL', 'MATIC', 'HYPE', 'SOL'];
        const isNative = !selectedKey.token_address ||
          selectedKey.token_address === '0x0000000000000000000000000000000000000000' ||
          nativeSymbols.includes(selectedKey.token_symbol?.toUpperCase());
        const isSolana = selectedKey.chain_name?.toLowerCase().includes('solana') || !!selectedKey.solana_address;
        const isTron = selectedKey.chain_name?.toLowerCase().startsWith('tron') || getChainType(selectedKey.ethereum_address) === 'tron';

        if (isTron) {
          // Tron contract uses a fixed-fee model (TRX flat fee + bps for TRC-20).
          // The detailed breakdown is enforced by the contract; we skip the
          // client-side estimate for v1 and let the relayInfo defaults drive
          // the displayed max_relayer_fee in the UI.
          setFeeBreakdown(null);
          return;
        }

        if (isSolana) {
          try {
            const treasuryState = await solanaInternullService.getTreasuryState();
            if (treasuryState) {
              setFeeBreakdown(estimateSolanaFeeBreakdown(
                treasuryState.protocolFeeBps,
                treasuryState.fixedRelayerFeeLamports,
                selectedKey.denomination_wei,
                selectedKey.denomination,
                selectedKey.token_decimals || 9,
              ));
              return;
            }
          } catch (e) {
            console.warn('Failed to read Solana treasury state:', e.message);
          }
          setFeeBreakdown(null);
          return;
        }

        let gasPrice = null;
        if (isNative) {
          try {
            const chainId = selectedKey.chain_id || selectedKey.chainId;
            let rpcUrl = multiTokenTreasuryService.getRpcUrlForChain(Number(chainId))
              || multiTokenTreasuryService.getRpcUrlForChain(String(chainId));
            if (!rpcUrl && selectedKey.chain_name) {
              rpcUrl = multiTokenTreasuryService.getRpcUrlByChainName(selectedKey.chain_name);
            }
            if (rpcUrl) {
              gasPrice = await new ethers.providers.JsonRpcProvider(rpcUrl).getGasPrice();
            }
          } catch (e) { /* ignore */ }
        }

        const chainId = selectedKey.chain_id || selectedKey.chainId;
        let effectiveRelayInfo = relayInfo;
        if (chainId) {
          const onChainBps = await getProtocolFeeBpsFromContract(chainId);
          if (onChainBps !== null) {
            effectiveRelayInfo = { ...relayInfo, protocol_fee_bps: onChainBps };
          }
        }

        setFeeBreakdown(estimateFeeBreakdown(
          effectiveRelayInfo,
          selectedKey.denomination_wei,
          selectedKey.denomination,
          isNative,
          gasPrice,
          selectedKey.token_decimals,
          chainId,
        ));
      } catch (e) {
        console.warn('Fee estimation failed:', e.message);
        setFeeBreakdown(null);
      } finally {
        setFeeLoading(false);
      }
    };

    fetchFees();
  }, [open, selectedKey, relayInfo, relayerHasBalance]);

  if (!selectedKey) return null;

  const chainName = selectedKey.chain_name || '';
  const isSolana = chainName.toLowerCase().includes('solana') || !!selectedKey.solana_address;
  const chainDisplayName = selectedKey.chainDisplayName || chainName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const tokenSymbol = selectedKey.token_symbol || 'ETH';
  const denomination = selectedKey.denomination || '0';
  const relayAvailable = !!relayInfo && relayerHasBalance;
  const feesExceedAmount = feeBreakdown && !feeBreakdown.viable;
  const subtleText = isDark ? '#888' : '#999';

  // Validate the recipient strictly matches the target chain. Without this,
  // Tron `T...` addresses would pass the legacy `!startsWith('0x')` check and
  // be sent through the Solana withdrawal path (or vice-versa) — that would
  // break the cross-chain privacy guarantee.
  const addressValid = recipientAddress && (
    isSolana
      ? getChainType(recipientAddress) === 'solana'
      : /^0x[a-fA-F0-9]{40}$/.test(recipientAddress)
  );

  return (
    <Dialog
      open={open}
      onClose={withdrawing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#1a1a1a' : '#fff',
          backgroundImage: 'none',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Unlock Funds</Typography>
      </DialogTitle>

      <DialogContent>
        {/* Key Summary */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: isDark ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.4)',
            bgcolor: isDark ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.08)',
          }}
        >
          {getChainLogoUrl(chainName) && (
            <Box
              component="img"
              src={getChainLogoUrl(chainName)}
              alt={chainName}
              sx={{ width: 32, height: 32, borderRadius: '50%' }}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {denomination} {tokenSymbol}
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666' }}>
              on {chainDisplayName}
            </Typography>
          </Box>
          <Chip
            icon={relayAvailable ? <FlashOn fontSize="small" /> : <LocalGasStation fontSize="small" />}
            label={relayAvailable ? 'Gas-Free' : 'Direct'}
            size="small"
            sx={{
              bgcolor: relayAvailable ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.08)',
              color: relayAvailable ? '#4caf50' : (isDark ? '#aaa' : '#666'),
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Fee Breakdown */}
        {relayAvailable && !withdrawing && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              border: '1px solid',
              borderColor: feesExceedAmount
                ? (isDark ? 'rgba(244,67,54,0.4)' : 'rgba(244,67,54,0.3)')
                : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'),
            }}
          >
            {feeLoading ? (
              <Box>
                <Skeleton width="60%" height={20} />
                <Skeleton width="80%" height={20} />
              </Box>
            ) : feeBreakdown ? (
              <>
                {feeBreakdown.protocolFeeBps > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: subtleText }}>
                      Protocol fee ({feeBreakdown.protocolFeeBps / 100}%)
                    </Typography>
                    <Typography variant="caption" sx={{ color: subtleText }}>
                      -{feeBreakdown.protocolFee} {tokenSymbol}
                    </Typography>
                  </Box>
                )}
                {feeBreakdown.isSolana && parseFloat(feeBreakdown.relayerFee) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: subtleText }}>Relayer fee</Typography>
                    <Typography variant="caption" sx={{ color: subtleText }}>-{feeBreakdown.relayerFee} {tokenSymbol}</Typography>
                  </Box>
                )}
                {feeBreakdown.isNative && parseFloat(feeBreakdown.gasReimbursement) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: subtleText }}>Gas (est.)</Typography>
                    <Typography variant="caption" sx={{ color: subtleText }}>
                      ~{parseFloat(feeBreakdown.gasReimbursement).toFixed(6)} {tokenSymbol}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 0.75, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>You receive</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: feesExceedAmount ? '#f44336' : '#4caf50' }}>
                    {feesExceedAmount
                      ? 'Fees exceed amount'
                      : `~${parseFloat(feeBreakdown.netAmount).toFixed(6)} ${tokenSymbol}`
                    }
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography variant="caption" sx={{ color: subtleText }}>Fee estimate unavailable</Typography>
            )}
          </Box>
        )}

        {/* Fees exceed warning */}
        {feesExceedAmount && !withdrawing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: onSwitchToDirect ? 1 : 0 }}>
              Denomination too small for gas-free relay.
            </Typography>
            {onSwitchToDirect && (
              <Button size="small" variant="outlined" onClick={onSwitchToDirect} sx={{ textTransform: 'none' }}>
                Switch to Direct Withdrawal
              </Button>
            )}
          </Alert>
        )}

        {/* Relayer unavailable */}
        {!relayAvailable && !withdrawing && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Relayer unavailable — using direct withdrawal. Connect {isSolana ? 'Phantom' : 'MetaMask'} to {chainDisplayName}.
          </Alert>
        )}

        {/* Recipient */}
        {!withdrawing && (
          <TextField
            fullWidth
            size="small"
            label={isSolana ? 'Recipient Solana Address' : 'Recipient Address'}
            placeholder={isSolana ? 'Base58 Solana address...' : '0x...'}
            value={recipientAddress}
            onChange={(e) => onRecipientChange(e.target.value)}
            sx={{ mb: 1 }}
            autoFocus
          />
        )}

        {/* Progress */}
        {withdrawing && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {relayJobId ? (
              <RelayStatusTracker jobId={relayJobId} nodeUrl={relayNodeUrl} isDark={isDark} />
            ) : (
              <>
                <CircularProgress size={36} sx={{ color: '#22c55e', mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#666' }}>
                  {relayAvailable ? 'Submitting to relayer...' : 'Sending transaction...'}
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={withdrawing} sx={{ color: isDark ? '#aaa' : '#666' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onExecuteWithdrawal}
          disabled={withdrawing || !addressValid || feesExceedAmount}
          sx={{
            background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
            '&:hover': { background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)' },
            '&.Mui-disabled': { background: isDark ? '#333' : '#ccc' },
            px: 4,
          }}
        >
          {withdrawing ? 'Unlocking...' : relayAvailable ? 'Unlock (Gas-Free)' : 'Unlock to Wallet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimpleWithdrawalDialog;
